import React, { useEffect, useRef, useState } from 'react';
import { Box, Stack } from '@mui/material';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

// ===========================================================================
// CinematicHero
//
// Background composition (back to front):
//   1) <video>  — autoplay/muted/looped if `/assets/video/hero-shavuot.mp4`
//                 exists. Detected at mount via fetch(HEAD); if missing the
//                 element is never rendered, so we fall through to step 2.
//   2) Still photograph — the existing KBT beit-midrash shot, ALWAYS rendered
//                 below the video as a graceful fallback / poster frame.
//   3) Drifting greenery / petals — subtle SVG particles for a festive feel
//                 (Shavuot's tradition of flowers + greenery in shul).
//   4) Navy curtain — keeps the headline legible.
//
// To drop in a real video later, save an MP4 (1920x1080, ~15s loop, H.264,
// silent) at /public/assets/video/hero-shavuot.mp4 — no code change needed.
// ===========================================================================
export default function CinematicHero() {
  const ref = useRef(null);
  const navigate = useNavigate();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start'],
  });

  // Probe for the video file once at mount. The dev server's SPA fallback
  // returns index.html for missing paths, so a 200 alone isn't enough — we
  // also require the content-type to be video/* before mounting the player.
  const [hasVideo, setHasVideo] = useState(false);
  useEffect(() => {
    let alive = true;
    fetch('/assets/video/hero-shavuot.mp4', { method: 'HEAD' })
      .then((r) => {
        if (!alive) return;
        const ct = r.headers.get('content-type') || '';
        setHasVideo(r.ok && ct.startsWith('video/'));
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  // Photo: subtle zoom + parallax up as you scroll. Smaller scale range
  // than before — the wider range was repainting too much area on high-DPI
  // big screens (16" MBP retina ≈ 3456×2234) and contributing to the jitter.
  const photoScale = useTransform(scrollYProgress, [0, 1], [1.04, 1.12]);
  const photoY = useTransform(scrollYProgress, [0, 1], ['0%', '-8%']);

  // Headline: fades, drifts up, scales slightly.
  const titleScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const titleOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const titleY = useTransform(scrollYProgress, [0, 1], ['0%', '-22%']);

  // Navy curtain deepens with scroll.
  const curtainOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.92]);

  return (
    <Box
      ref={ref}
      component="section"
      sx={{
        position: 'relative',
        height: '160vh',
        background: 'var(--navy-900)',
      }}
    >
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          height: '100vh',
          width: '100%',
          overflow: 'hidden',
          display: 'grid',
          gridTemplateRows: 'auto 1fr auto',
          color: '#fff',
        }}
      >
        {/* 1) Still photograph — always present, acts as poster/fallback.
            Cinematic scale + parallax is driven by framer-motion scroll
            transforms below. We deliberately do NOT layer a CSS keyframe
            animation on the same element — two transform sources writing
            to the same style every frame caused visible jitter on high-DPI
            big displays. `willChange: transform` promotes this to its own
            GPU layer so the scroll transform is composited cheaply. */}
        <motion.div
          style={{
            position: 'absolute',
            inset: '-4% -2%',
            backgroundImage: "url('/assets/images/kbt/hero-sy.jpg')",
            backgroundSize: 'cover',
            backgroundPosition: 'center 55%',
            scale: photoScale,
            y: photoY,
            zIndex: 0,
            willChange: 'transform',
            transform: 'translateZ(0)',
          }}
          aria-hidden
        />

        {/* 2) Optional hero video — only mounts if the MP4 exists. */}
        {hasVideo && (
          <Box
            component="video"
            src="/assets/video/hero-shavuot.mp4"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            aria-hidden
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              zIndex: 0,
              // Festive warm grade — soft gold tint, slight contrast lift.
              filter: 'saturate(1.06) contrast(1.05) brightness(1.02)',
            }}
          />
        )}

        {/* 3) Drifting greenery / petal particles for the Shavuot motif.
            No mix-blend-mode — that was forcing per-frame composite blending
            of the layers underneath on big high-DPI displays. Slight opacity
            bump compensates for losing the screen blend. */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            pointerEvents: 'none',
            opacity: 0.7,
            backgroundImage:
              `radial-gradient(circle at 20% 35%, rgba(212,178,107,0.18) 0%, transparent 14%),
               radial-gradient(circle at 78% 22%, rgba(255,224,196,0.16) 0%, transparent 12%),
               radial-gradient(circle at 12% 80%, rgba(120,180,140,0.16) 0%, transparent 18%),
               radial-gradient(circle at 88% 78%, rgba(212,178,107,0.14) 0%, transparent 16%)`,
            animation: 'driftSlow 28s ease-in-out infinite alternate',
            willChange: 'transform',
          }}
        />

        {/* Warm golden top wash — late-morning Yom-Tov light coming in.
            Plain alpha over the photo; no blend mode (cheaper on big screens). */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            pointerEvents: 'none',
            background:
              'linear-gradient(180deg, rgba(255,212,140,0.12) 0%, rgba(255,200,120,0.05) 22%, transparent 50%)',
          }}
        />

        {/* Navy curtain over the photo */}
        <motion.div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(180deg, rgba(7,6,26,0.55) 0%, rgba(7,6,26,0.42) 38%, rgba(7,6,26,0.86) 100%)',
            opacity: curtainOpacity,
            zIndex: 1,
          }}
          aria-hidden
        />

        {/* Navy spotlight at bottom for legibility of CTAs */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(ellipse 80% 60% at 50% 78%, rgba(36,34,88,0.65), transparent 70%)',
            zIndex: 2,
            pointerEvents: 'none',
          }}
          aria-hidden
        />

        {/* Top kicker rail */}
        <Box
          sx={{
            position: 'relative',
            zIndex: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: { xs: 3, md: 6 },
            pt: { xs: 14, md: 16 },
            fontFamily: 'var(--font-mono)',
            fontSize: 10.5,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: 'var(--teal-100)',
            gap: 3,
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            Kehilat Bnei Torah  ·  Los Angeles
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            style={{ display: 'flex', alignItems: 'center', gap: 8 }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: 'var(--teal-300)',
                display: 'inline-block',
              }}
            />
            9017 W. Pico Blvd.
          </motion.div>
        </Box>

        {/* Centered stage — title */}
        <Box
          sx={{
            position: 'relative',
            zIndex: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: { xs: 3, md: 6 },
          }}
        >
          <motion.div style={{ scale: titleScale, opacity: titleOpacity, y: titleY }}>
            <Stack alignItems="center" spacing={3.5}>
              <motion.div
                initial={{ opacity: 0, letterSpacing: '0.6em' }}
                animate={{ opacity: 1, letterSpacing: '0.28em' }}
                transition={{ duration: 1.4, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 12,
                  color: 'var(--teal-300)',
                  textTransform: 'uppercase',
                }}
              >
                KBT,  A Kehila
              </motion.div>

              <Box sx={{ textAlign: 'center', maxWidth: 1100 }}>
                <motion.h1
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1.2, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
                  style={{
                    margin: 0,
                    fontFamily: 'var(--font-display)',
                    fontWeight: 700,
                    lineHeight: 0.95,
                    letterSpacing: '-0.02em',
                    color: '#fff',
                    fontSize: 'clamp(48px, 8.8vw, 132px)',
                    textShadow: '0 2px 40px rgba(7,6,26,0.4)',
                  }}
                >
                  More than just
                  <br />
                  <span
                    style={{
                      fontStyle: 'italic',
                      fontWeight: 500,
                      color: 'var(--teal-300)',
                    }}
                  >
                    a Beit Knesset.
                  </span>
                </motion.h1>
              </Box>

              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.95, ease: [0.22, 1, 0.36, 1] }}
                style={{
                  fontFamily: 'var(--font-body)',
                  fontWeight: 400,
                  fontSize: 'clamp(16px, 1.6vw, 19px)',
                  color: 'rgba(255,255,255,0.88)',
                  maxWidth: 620,
                  textAlign: 'center',
                  lineHeight: 1.55,
                }}
              >
                A synagogue, a beit midrash, a kehilla — revolutionizing the world of
                Torah in Los Angeles. Our goal: one million hours of Torah-learning
                across the city over the next decade.
              </motion.div>

              {/* CTA pair */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 1.15, ease: [0.22, 1, 0.36, 1] }}
                style={{ display: 'flex', gap: 16, marginTop: 12, flexWrap: 'wrap', justifyContent: 'center' }}
              >
                <Box
                  onClick={() => navigate('/membership2026')}
                  sx={{
                    cursor: 'pointer',
                    px: 3.5,
                    py: 1.6,
                    background: 'var(--teal-500)',
                    color: '#fff',
                    fontFamily: 'var(--font-body)',
                    fontWeight: 700,
                    fontSize: 12.5,
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                    borderRadius: 999,
                    transition: 'background 250ms ease, transform 250ms ease',
                    '&:hover': { background: 'var(--teal-700)', transform: 'translateY(-2px)' },
                  }}
                >
                  Become a member
                </Box>
                <Box
                  onClick={() => navigate('/donate-now')}
                  sx={{
                    cursor: 'pointer',
                    px: 3.5,
                    py: 1.6,
                    background: 'transparent',
                    color: '#fff',
                    border: '1.5px solid rgba(255,255,255,0.6)',
                    fontFamily: 'var(--font-body)',
                    fontWeight: 700,
                    fontSize: 12.5,
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                    borderRadius: 999,
                    transition: 'background 250ms ease, color 250ms ease, border-color 250ms ease',
                    '&:hover': {
                      background: '#fff',
                      color: 'var(--navy-500)',
                      borderColor: '#fff',
                    },
                  }}
                >
                  Help spread Torah
                </Box>
              </motion.div>
            </Stack>
          </motion.div>
        </Box>

        {/* Bottom strap */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-end"
          sx={{
            position: 'relative',
            zIndex: 3,
            px: { xs: 3, md: 6 },
            pb: { xs: 4, md: 5 },
            color: 'var(--teal-100)',
          }}
        >
          <Stack spacing={1.2}>
            <Box
              sx={{
                fontFamily: 'var(--font-mono)',
                fontSize: 10.5,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
              }}
            >
              Scroll to enter
            </Box>
            <Box
              component={motion.div}
              animate={{ height: ['48px', '24px', '48px'] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
              sx={{
                width: '1px',
                height: '48px',
                background: 'rgba(255,255,255,0.7)',
                ml: 1,
              }}
            />
          </Stack>

          <Box
            sx={{
              fontFamily: 'var(--font-mono)',
              fontSize: 10.5,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              textAlign: 'right',
            }}
          >
            1,000,000 hours of Torah · LA · 10 yrs
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}
